import {
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';
import { HttpError, RQAPICall } from 'store/APIClient';

import { cleanChartData } from 'helpers/chartHelper';
import { camelize } from 'helpers/syntaxHelper';
import { translateBlock } from 'i18n';
import { transformQueryToDataQuery } from 'helpers/queryHelpers';

/**
 * Get the list of data and columns for a given table (metric)
 */
export const useTableDataQuery = (
    tableName: string,
    params: Pick<
        UseQueryOptions<GetDataResponse, HttpError>,
        'enabled' | 'initialData'
    >,
): UseQueryResult<GetDataResponse, HttpError> => {
    const query: QueryT = {
        metrics: [{ field: tableName, aggregation: 'list' }],
        dimensions: [],
        filters: [],
        add_default_dimensions: true,
    };

    return useQuery({
        queryKey: ['getTableData', tableName],
        queryFn: async () => {
            const response: { content: { data: GetDataResponse } } =
                await RQAPICall({
                    endpoint: `query/get_data/v2`,
                    method: 'POST',
                    body: transformQueryToDataQuery(query),
                });
            const { columns, data } = response.content.data;
            // We apply the same logic than in the rawDataModal/models.js setQueryResult function
            // We remove column types since they contain id, which doesn't make sense to the user.
            const enhancedColumns = columns
                .filter((col) => col.type !== 'table')
                .map((col) => ({
                    ...col,
                    key: camelize(col.key),
                    label: translateBlock(col.key),
                    type: col.type,
                }));

            return {
                columns: enhancedColumns,
                data: cleanChartData(data, enhancedColumns),
            };
        },
        refetchOnWindowFocus: false,
        ...params,
    });
};
